// candidate-commit-message-builder.ts
//
// Commit message construction for the pushed candidate commit, split out of
// candidate-commit-without-controller-paths.ts to keep that file ≤200 lines.
// Leaf module: pure string formatting, no git I/O.

export function buildCandidateCommitMessage(input: {
  missionId: string;
  candidateSha: string;
  baseSha: string;
  stripped: readonly string[];
  /** `<sha> <subject>` per ticket commit on the mission branch (base..HEAD), newest first — the history the squash folds away. */
  ticketCommits?: readonly string[];
  uncoveredAreas?: readonly string[];
  remainingWork?: readonly string[];
  uncoveredDeclaredNone?: readonly string[];
  uncoveredNotDeclared?: readonly string[];
  /**
   * D16 (H14, 2026-09-29): `config.release.deploy_recipe.recipe_id`, or `null`
   * when none is configured — printed as an `Autopilot-Deploy:` trailer so
   * whoever merges the MR knows whether approving G3 deploys anything.
   * Absent ⇒ no trailer (callers that predate release config).
   */
  deployRecipeId?: string | null;
}): string {
  const commits = input.ticketCommits ?? [];
  const uncovered = input.uncoveredAreas ?? [];
  const remaining = input.remainingWork ?? [];
  // Dogfood ME-2026-d74b4a: an explicit `[]` is a claim the reviewer can hold
  // the worker to; an absent field is silence. Both are printed per ticket so
  // neither can read as "everything verified".
  const perTicketStatus = [
    ...(input.uncoveredDeclaredNone ?? []).map(
      (ticket) => `- ${ticket}: worker khai không có phần chưa kiểm`,
    ),
    ...(input.uncoveredNotDeclared ?? []).map(
      (ticket) => `- ${ticket}: worker không khai — không có nghĩa là đã kiểm hết`,
    ),
  ];
  return [
    `candidate(${input.missionId}): mission tree without controller-owned paths`,
    "",
    // Code review P3 M2: since CXR-04 these paths carry the BASE's content
    // (none ⇒ absent), never "removed" — say what the tree actually holds.
    `Derived from mission HEAD ${input.candidateSha} with ${input.stripped.join(", ")} reset to the base's content (absent where the base has none);`,
    "mission changes under them stay in the mission worktree (tamper evidence) and are not part of this merge request.",
    ...(commits.length > 0 ? ["", "Ticket commits:", ...commits.map((line) => `- ${line}`)] : []),
    // A2 (spec §A2 block 3/4): this message becomes the draft MR description,
    // so the reviewer sees the coverage limit. An ABSENT declaration is
    // printed as such — silence must never read as "everything verified"
    // (evidence-discipline ban 6).
    "",
    "Chưa kiểm chứng:",
    ...uncovered.map((line) => `- ${line}`),
    ...perTicketStatus,
    ...(uncovered.length === 0 && perTicketStatus.length === 0
      ? [
          "- (worker không khai — không có nghĩa là đã kiểm hết; reviewer tự xác định phạm vi chưa chạy)",
        ]
      : []),
    ...(remaining.length > 0 ? ["", "Còn lại:", ...remaining.map((line) => `- ${line}`)] : []),
    "",
    `Autopilot-Mission: ${input.missionId}`,
    `Autopilot-Candidate: ${input.candidateSha}`,
    `Autopilot-Base: ${input.baseSha}`,
    ...(input.deployRecipeId === undefined
      ? []
      : [
          `Autopilot-Deploy: ${input.deployRecipeId === null ? "not-configured" : `recipe ${input.deployRecipeId}`} (tại lúc push)`,
        ]),
  ].join("\n");
}
